import { useEffect, useState } from 'react';
import {
  ScanLine, CheckCircle2, XCircle, AlertTriangle, Loader2, LogIn, LogOut, History,
} from 'lucide-react';
import * as api from '../../lib/api';
import { useShiftStore } from '../../store/useShiftStore';
import { formatCents } from '../../lib/format';
import NoFacility from '../../components/attendant/NoFacility';

/**
 * Gate verification.
 *
 * Designed for a tablet held in one hand at a gate: large targets, a verdict
 * readable at arm's length, and the tenant record next to it so the attendant
 * can compare the rig in front of them against what is on file.
 */
export default function GatePage() {
  const facilityId = useShiftStore((s) => s.facilityId);
  const facilities = useShiftStore((s) => s.facilities);
  const [code, setCode] = useState('');
  const [plate, setPlate] = useState('');
  const [direction, setDirection] = useState('entry');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);

  const refreshLog = async () => {
    if (!facilityId) return;
    try {
      const { events } = await api.gateLog(facilityId, 12);
      setLog(events);
    } catch { /* the log is supporting detail; never block the gate on it */ }
  };

  useEffect(() => { setResult(null); refreshLog(); }, [facilityId]);

  const submit = async (event) => {
    event.preventDefault();
    if (!code.trim() || !facilityId) return;
    setBusy(true);
    setError(null);
    try {
      const verdict = await api.verifyGate(facilityId, {
        gate_code: code.trim(),
        direction,
        plate_read: plate.trim() || null,
      });
      setResult(verdict);
      setCode('');
      setPlate('');
      refreshLog();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Without a facility the form submits into a no-op: the button enables on a
  // typed code but the handler aborts before any request. At a gate, a control
  // that looks live and silently does nothing is worse than no control.
  if (!facilityId) return <NoFacility hasAnyFacility={facilities.length > 0} />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Verify entry</h1>
      <p className="mt-1 text-sm text-slate-600">
        Enter the gate code or scan the tenant&apos;s QR. Every attempt is logged either way.
      </p>

      <form onSubmit={submit} className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="mb-4 grid grid-cols-2 gap-2">
          {[['entry', LogIn, 'Entering'], ['exit', LogOut, 'Leaving']].map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => { setDirection(value); setResult(null); }}
              aria-pressed={direction === value}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-bold transition ${
                direction === value
                  ? 'border-brand-600 bg-brand-50 text-brand-800'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" /> {label}
            </button>
          ))}
        </div>

        <label className="field-label mb-1" htmlFor="gate-code">Gate code</label>
        <input
          id="gate-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            // Drop the previous verdict the instant a new code is typed. A
            // stale green "Open the gate" sitting above a different code is
            // exactly how the wrong vehicle gets admitted.
            if (result) setResult(null);
          }}
          autoFocus
          autoComplete="off"
          inputMode="text"
          placeholder="4417"
          className="w-full rounded-xl border border-slate-300 px-4 py-4 text-center font-mono text-3xl font-extrabold tracking-[0.3em] uppercase outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
        />

        <label className="field-label mb-1 mt-4" htmlFor="plate">Plate seen (optional)</label>
        <input
          id="plate" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
          autoComplete="off" placeholder="RV42TIF" className="field-input font-mono uppercase"
        />

        <button type="submit" disabled={busy || !code.trim()} className="btn-primary mt-4 w-full py-4 text-base">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <ScanLine className="h-5 w-5" aria-hidden="true" />}
          {busy ? 'Checking…' : 'Check access'}
        </button>

        {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
      </form>

      {result && <Verdict result={result} />}

      <section className="mt-8">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <History className="h-4 w-4 text-brand-500" aria-hidden="true" /> Recent activity
        </h2>
        <ul className="mt-3 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {log.length === 0 && <li className="p-4 text-sm text-slate-500">No gate activity yet.</li>}
          {log.map((e) => (
            <li key={e.id} className="flex items-center gap-3 p-3 text-xs">
              {e.was_granted
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                : <XCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />}
              <span className="w-16 shrink-0 font-semibold text-slate-700">{e.direction}</span>
              <span className="min-w-0 flex-1 truncate text-slate-700">
                {e.first_name ? `${e.first_name} ${e.last_name}` : 'Unknown credential'}
                {e.unit_number && <span className="text-slate-400"> · {e.unit_number}</span>}
                {e.denial_reason && <span className="text-rose-700"> — {e.denial_reason}</span>}
              </span>
              {e.plate_read && <span className="shrink-0 font-mono text-slate-400">{e.plate_read}</span>}
              <time className="shrink-0 text-slate-400">
                {new Date(e.occurred_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </time>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Verdict({ result }) {
  const { granted, reason, tenant, advisories } = result;

  return (
    <div
      role="status"
      aria-live="assertive"
      className={`mt-4 overflow-hidden rounded-2xl border-2 ${
        granted ? 'border-emerald-500 bg-emerald-50' : 'border-rose-500 bg-rose-50'}`}
    >
      <div className="flex items-center gap-3 p-5">
        {granted
          ? <CheckCircle2 className="h-10 w-10 shrink-0 text-emerald-600" aria-hidden="true" />
          : <XCircle className="h-10 w-10 shrink-0 text-rose-600" aria-hidden="true" />}
        <div className="min-w-0">
          <p className={`text-2xl font-extrabold ${granted ? 'text-emerald-900' : 'text-rose-900'}`}>
            {granted ? 'Open the gate' : 'Do not admit'}
          </p>
          {reason && <p className="text-sm font-semibold text-rose-800">{reason}</p>}
        </div>
      </div>

      {tenant && (
        <div className="border-t border-current/10 bg-white/60 p-5">
          <p className="text-base font-extrabold text-slate-900">{tenant.name}</p>
          <p className="text-xs text-slate-600">
            {tenant.lease_number} · Unit {tenant.unit_number} ({tenant.space_type})
          </p>

          <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Pair label="Vehicle" value={tenant.vehicle_label ?? '—'} />
            <Pair label="Length" value={tenant.vehicle_length_ft ? `${tenant.vehicle_length_ft}ft` : '—'} />
            <Pair label="Plate" value={tenant.license_plate ?? '—'} mono />
            <Pair label="Phone" value={tenant.phone ?? '—'} />
          </dl>

          {advisories?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {advisories.map((a) => (
                <li key={a.message} className="flex items-start gap-2 rounded-lg bg-amber-100 p-2.5 text-xs font-semibold text-amber-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {a.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Pair({ label, value, mono }) {
  return (
    <div>
      <dt className="font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`mt-0.5 font-bold text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
